import { hideOneMindBot } from '@thoughtspot/1mind';
import { blinkConstants } from '@thoughtspot/blink-constants';
import { initFeatureFlagsContext } from '@thoughtspot/blink-context';
import { envFlags, FLAGS } from '@thoughtspot/env-flags';
import {
    getLDContext,
    initialize as initFeatureService,
} from '@thoughtspot/feature-service';
import { closeIntercom } from '@thoughtspot/intercom';
import { create } from '@thoughtspot/logger';
import { handleLoginResponse } from '@thoughtspot/session-service';
import {
    getOidcLoginUriFromIssuers,
    isOidcEnabled,
    isOktaEnabled,
    setSystemConfig,
} from '@thoughtspot/system-config-service';
import React from 'react';
import { BehaviorSubject } from 'rxjs';
import useFetch, { CachePolicies } from 'use-http';
import {
    initSessionService,
    isPartitionedCookieEnabled,
    shouldLogoutWithRedirect,
} from '/@services/system/config-service/session-service';
import { postAuthentication } from '/@services/system/lifecycle-hooks-service/lifecycle-hooks-service';
import { dispatchNonMemoEvent } from '/@utils/event-bridge/event-bridge';
import {
    overrideRequestOptions,
    setCSRFToken,
} from '/@utils/http-request-polyfill';
import { PERF_EVENT,PERF_KEYS, setPerfLog } from '/@utils/perf-log';
import { PerformanceMarkers } from '../../utils/common-enum';
import { EventType, IncomingEventType } from '../../utils/embed.util';
import { useMutation } from '../../utils/error-handling/apollo-hooks-wrappers/custom-apollo-hooks';
import { invalidateSessionInfoCallCache } from '../../utils/service-worker.util';
import {
    DoLogoutDocument,
    DoLogoutMutation,
    DoLogoutMutationVariables,
} from '../generated/graphql-types';
import { initCustomStylingService } from './config-service/custom-styling-service';

const Logger = create('login-service');
const AUTHENTICATION_TIMEOUT = 30000;

export interface MessageEventData {
    type: string;
    data: {
        info?: any;
        [key: string]: any;
    };
}

export type AuthenticationState = {
    isAuthenticated: boolean;
    authenticationChangeInProgress: boolean;
};

export interface UpdateServiceOptions {
    /** Override authentication state during updates */
    preauthInfoUpdated?: boolean;
}

export interface UseUpdateSessionInfoOptions {
    /** Override authentication state during updates */
    preauthInfoUpdated?: boolean;
}

const isAuthenticatedSubject: BehaviorSubject<AuthenticationState> = new BehaviorSubject<
    AuthenticationState
>({
    isAuthenticated: undefined,
    authenticationChangeInProgress: true,
});

export const updateServices = async (
    { info, config, style, launchdarklyFlags }: any,
    options: UpdateServiceOptions = {},
) => {
    const { preauthInfoUpdated = false } = options;

    if (config) {
        setSystemConfig(config);
    }
    if (style) {
        initCustomStylingService(style);
    }
    if (info) {
        await postAuthentication(info, options);
    }
    if (launchdarklyFlags) {
        initFeatureFlagsContext({ launchdarklyFlags });
        initFeatureService(getLDContext(), launchdarklyFlags, {
            shouldOverride: preauthInfoUpdated,
        });
    }
};

export const getIsUserAuthenticated = (): Promise<boolean> => {
    if (isAuthenticatedSubject.hasError || isAuthenticatedSubject.isStopped) {
        const latestAuthState = isAuthenticatedSubject.getValue();
        if (
            latestAuthState.isAuthenticated !== true ||
            latestAuthState.authenticationChangeInProgress === true
        ) {
            return Promise.resolve(false);
        }
        return Promise.resolve(true);
    }
    return new Promise<boolean>(resolve => {
        // eslint-disable-next-line promise/param-names
        const failSafe = new Promise<boolean>(resolveFailSafe => {
            setTimeout(() => resolveFailSafe(false), AUTHENTICATION_TIMEOUT);
        });
        // eslint-disable-next-line promise/param-names
        const authPromise = new Promise<boolean>(resolveAuth => {
            const waitForAuthenticaiton = () => {
                isAuthenticatedSubject.subscribe(
                    (authState: AuthenticationState) => {
                        if (authState.authenticationChangeInProgress) {
                            return null;
                        }
                        return resolveAuth(authState.isAuthenticated);
                    },
                );
            };
            return waitForAuthenticaiton();
        });
        return Promise.race([authPromise, failSafe]).then(resolve);
    });
};

export const useAuthenticatedState = () => {
    const [authState, setAuthState] = React.useState<AuthenticationState>(
        isAuthenticatedSubject.getValue(),
    );
    React.useEffect(() => {
        const subscription = isAuthenticatedSubject.subscribe(
            (newAuthState: AuthenticationState) => {
                setAuthState(newAuthState);
            },
        );
        return () => {
            subscription.unsubscribe();
        };
    }, []);
    return authState;
};

export const useAuthenticate = async () => {
    if (!isAuthenticatedSubject.getValue().authenticationChangeInProgress) {
        isAuthenticatedSubject.next({
            isAuthenticated: undefined,
            authenticationChangeInProgress: true,
        });
    }
    let fetchResponse;

    if ((window as any)?.preauthInfo) {
        fetchResponse = (window as any)?.preauthInfo;

        const data = await fetchResponse.clone().json();

        if (data) {
            updateServices(data);
        }

        if (data.status === 200) {
            isAuthenticatedSubject.next({
                isAuthenticated: true,
                authenticationChangeInProgress: false,
            });
        } else {
            dispatchNonMemoEvent(EventType.AuthFailure, null);
            isAuthenticatedSubject.next({
                isAuthenticated: false,
                authenticationChangeInProgress: false,
            });
        }
    } else {
        const fetchResponse = useFetch(
            '/prism/preauth/info',
            {
                method: 'GET',
                headers: {
                    'x-requested-by': 'ThoughtSpot',
                },
                interceptors: {
                    request: overrideRequestOptions,
                },
            },
            [],
        );
        const { data, response } = fetchResponse;
        React.useEffect(() => {
            if (data) {
                updateServices(data);
            }
            if (response.ok) {
                const csrfToken = response.headers?.get('X-CSRF-Token');

                if (csrfToken) {
                    setCSRFToken(csrfToken);
                }

                isAuthenticatedSubject.next({
                    isAuthenticated: true,
                    authenticationChangeInProgress: false,
                });
            } else if (response.ok === false) {
                setCSRFToken('');
                dispatchNonMemoEvent(EventType.AuthFailure, null);
                isAuthenticatedSubject.next({
                    isAuthenticated: false,
                    authenticationChangeInProgress: false,
                });
            }
        }, [data, response.ok]);
    }

    return fetchResponse;
};

/**
 * Demo login for a public user. Doesn't require a username or password.
 */
export const useDemoLogin = () => {
    const { data, post, error, loading, response } = useFetch(
        '/prism/preauth/demo/login',
        {
            method: 'POST',
            headers: {
                'Content-Type':
                    'application/x-www-form-urlencoded;charset=UTF-8',
                'x-requested-by': 'ThoughtSpot',
            },
            cachePolicy: CachePolicies.NO_CACHE,
        },
    );
    React.useEffect(() => {
        if (response.ok) {
            updateServices(data);
            isAuthenticatedSubject.next({
                isAuthenticated: true,
                authenticationChangeInProgress: false,
            });
        } else if (response.ok === false) {
            dispatchNonMemoEvent(EventType.AuthFailure, null);
            isAuthenticatedSubject.next({
                isAuthenticated: false,
                authenticationChangeInProgress: false,
            });
        }
    }, [data, response]);
    return {
        error,
        loading,
        login: () => {
            isAuthenticatedSubject.next({
                isAuthenticated: undefined,
                authenticationChangeInProgress: true,
            });
            (window as any).preauthInfo = post();
            return (window as any).preauthInfo;
        },
        response,
    };
};

export const useLogin = () => {
    const { data, post, error, loading, response } = useFetch(
        '/prism/preauth/login',
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-requested-by': 'ThoughtSpot',
            },
            cachePolicy: CachePolicies.NO_CACHE,
        },
    );
    function login(username: string, password: string, rememberMe: boolean) {
        isAuthenticatedSubject.next({
            isAuthenticated: undefined,
            authenticationChangeInProgress: true,
        });
        (window as any).preauthInfo = post('', {
            username,
            password,
            rememberMe,
        });
        return (window as any).preauthInfo;
    }
    React.useEffect(() => {
        if (response.ok) {
            const csrfToken = response.headers?.get('X-CSRF-Token');

            if (csrfToken) {
                setCSRFToken(csrfToken);
            }
            updateServices(data);
            isAuthenticatedSubject.next({
                isAuthenticated: true,
                authenticationChangeInProgress: false,
            });
        } else if (response.ok === false) {
            setCSRFToken('');
            dispatchNonMemoEvent(EventType.AuthFailure, null);
            isAuthenticatedSubject.next({
                isAuthenticated: false,
                authenticationChangeInProgress: false,
            });
        }
    }, [data, response]);

    return {
        error,
        loading,
        login,
        response,
    };
};

const getPreauthInfoPromise = () => {
    const overrideOrgId = envFlags.getValue(FLAGS.overrideOrgId.name);
    const allowOrgOverride = overrideOrgId != null && overrideOrgId !== '-1';
    const headers = allowOrgOverride
        ? {
              'x-requested-by': 'ThoughtSpot',
              'x-thoughtspot-orgsoverride': overrideOrgId,
          }
        : {
              'x-requested-by': 'ThoughtSpot',
          };

    return fetch('/prism/preauth/info', {
        method: 'GET',
        headers,
    });
};

/*
 * This function handles the preauth info for the embed use case.
 * 1. It listens for a message from the parent window, and if the message
 * contains the preauth info, it resolves the main promise with the message data.
 * 2. If the message doesn't arrive in 2000ms, it fetches the preauth info from the server.
 * @returns {Promise} - A promise that resolves with the preauth info.
 */
export const handleEmbedPreauthInfo = () => {
    return new Promise((resolve, reject) => {
        const EMBED_INFO_TIMEOUT_MS = 2000;
        let hasReceivedMessageData = false;

        const handleInfoMessage = (
            event: MessageEvent & { data: MessageEventData },
        ) => {
            if (event?.data?.type === IncomingEventType.InfoSuccess) {
                (window as any).isWaitingForEmbedInfo = false;
                const embedInfoData = event?.data?.data;

                if (embedInfoData?.info) {
                    embedInfoData.embedData = true;
                    hasReceivedMessageData = true;
                    resolve(embedInfoData);
                }
                window.removeEventListener('message', handleInfoMessage);
            }
        };

        /* 
            Added a custom event listener as Event bridge is not available during info load
            
            NOTE: Event bridge init is called after Embed container is loaded. 
            TECH DEBT: Remove this custom event listener once 
                event bridge init is moved to embed init
        */
        window.addEventListener('message', handleInfoMessage);

        // Handle timeout and fallback to fetch
        setTimeout(() => {
            (window as any).isWaitingForEmbedInfo = false;
            window.removeEventListener('message', handleInfoMessage);

            if (!hasReceivedMessageData) {
                getPreauthInfoPromise()
                    .then(resolve)
                    .catch(reject);
            }
        }, EMBED_INFO_TIMEOUT_MS);
    });
};

export const fetchPreauthInfo = () => {
    if ((window as any)?.isWaitingForEmbedInfo) {
        return handleEmbedPreauthInfo();
    }

    return getPreauthInfoPromise();
};

export const useUpdateSessionInfo = async (
    onlyUpdateSessionInfo = false,
    options: UseUpdateSessionInfoOptions = {},
) => {
    const { preauthInfoUpdated = false } = options;
    let data;
    try {
        if (
            !isAuthenticatedSubject.getValue().authenticationChangeInProgress &&
            !preauthInfoUpdated &&
            !onlyUpdateSessionInfo
        ) {
            isAuthenticatedSubject.next({
                isAuthenticated: undefined,
                authenticationChangeInProgress: true,
            });
        }
        setPerfLog(PERF_KEYS.PREAUTH_INFO_READ, PERF_EVENT.START);
        if ((window as any)?.preauthInfo) {
            data = await (window as any)?.preauthInfo;
        } else {
            (window as any).preauthInfo = await fetchPreauthInfo();
            data = await (window as any)?.preauthInfo;
        }

        /*
         * When Tomcat is stopped or unreachable, nginx returns a 502
         * (Bad Gateway). When Tomcat is hung or too slow, nginx returns
         * a 504 (Gateway Timeout). In both cases, the error HTML page
         * is returned as the fetch() response body and its scripts
         * never execute. We load 503.html in an iframe (like nginx's
         * internal rewrite) so the browser URL stays unchanged. When
         * the server recovers, 503.html's checkServerAndReload calls
         * window.top.location.reload() which reloads the original URL.
         */
        if (
            (window as any).blink?.showWakeupPageWhenCallosumIsDown === 'true' &&
            (data?.status === 404 ||
                (data?.status >= 500 && data?.status < 600))
        ) {
            Logger.warn(
                `Info call returned ${data.status}, showing maintenance page`,
            );
            document.body.innerHTML =
                '<iframe src="/503.html" style="position:fixed;top:0;left:0;width:100%;height:100%;border:none;z-index:2147483647"></iframe>';
            return;
        }

        const csrfToken = data?.embedData
            ? data['X-CSRF-Token']
            : await data.clone()?.headers?.get('X-CSRF-Token');
        setCSRFToken(csrfToken);

        const authInfo = data?.embedData ? data : await data.clone().json();
        setPerfLog(PERF_KEYS.PREAUTH_INFO_READ, PERF_EVENT.COMPLETE);
        performance.mark(PerformanceMarkers.INFO_CALL_COMPLETED);
        if (onlyUpdateSessionInfo) {
            initSessionService(authInfo?.info);
            return;
        }
        setPerfLog(PERF_KEYS.UPDATE_SERVICES, PERF_EVENT.START);
        await updateServices(authInfo, options);
        setPerfLog(PERF_KEYS.UPDATE_SERVICES, PERF_EVENT.COMPLETE);
        if (data.status === 200) {
            // Only update authentication state if this is not a preauth info update
            // For preauth info updates, we just update services without changing auth state
            if (!preauthInfoUpdated) {
                isAuthenticatedSubject.next({
                    isAuthenticated: true,
                    authenticationChangeInProgress: false,
                });
            }
        } else {
            throw new Error(data.status.toString());
        }
    } catch (e) {
        setCSRFToken('');
        /* 
            This fix addresses the issue where the page prematurely displays the loginFailedMessage during Embedded SSO authentication.
            Previously, on receiving an `EventType.AuthFailure`, the iframe was replaced with a generic login failure message (`loginFailedMessage`),
            even while authentication was still in progress. This led to displaying an error message before authentication had fully failed.
            By adding the type to the event, the error message is now shown only after authentication has genuinely failed,
            preventing misleading error indications during the authentication process.
        */
        if (data?.status === 401) {
            dispatchNonMemoEvent(EventType.AuthFailure, {
                type: blinkConstants.UNAUTHENTICATED_FAILURE,
            });
        } else {
            dispatchNonMemoEvent(EventType.AuthFailure, null);
        }
        isAuthenticatedSubject.next({
            isAuthenticated: false,
            authenticationChangeInProgress: false,
        });
        Logger.warn('Error fetching session data', e);
    }
};

export const useDoLogout = () => {
    const [_doLogout, { data, error, loading }] = useMutation<
        DoLogoutMutation,
        DoLogoutMutationVariables
    >(DoLogoutDocument);

    // Helper function to handle the logout mutation and update the authentication state
    const handleLogout = () => {
        return _doLogout({}).then((): null => {
            hideOneMindBot(true);
            closeIntercom();
            invalidateSessionInfoCallCache();
            setCSRFToken('');
            isAuthenticatedSubject.next({
                isAuthenticated: false,
                authenticationChangeInProgress: false,
            });
            return null;
        });
    };

    // Helper function to perform OKTA-specific parallel actions
    const handleOktaLogout = (issuerLink: string) => {
        return Promise.all([
            fetch(`${issuerLink}/api/v1/sessions/me`, {
                method: 'DELETE',
                credentials: 'include',
            }).catch(() => {
                // Handle fetch error, if needed (optional)
            }),
            _doLogout({}),
        ]).then((): null => {
            closeIntercom();
            hideOneMindBot(true);
            invalidateSessionInfoCallCache();
            setCSRFToken('');
            isAuthenticatedSubject.next({
                isAuthenticated: false,
                authenticationChangeInProgress: false,
            });
            return null;
        });
    };

    return {
        data: data?.Session__doLogout,
        error,
        loading,
        doLogout: () => {
            isAuthenticatedSubject.next({
                isAuthenticated: undefined,
                authenticationChangeInProgress: true,
            });

            const issuerLink = getOidcLoginUriFromIssuers();

            // OKTA-enabled environment with partitioned cookies
            if (isOidcEnabled() && isOktaEnabled() && issuerLink) {
                const { origin } = new URL(issuerLink);
                if (!shouldLogoutWithRedirect()) {
                    return handleOktaLogout(origin);
                }
            }
            // Default flow: perform logout mutation
            return handleLogout();
        },
    };
};

export const doShortLivedLogin = () => {
    const { post, response, data } = useFetch(
        'callosum/v1/session/shortlivedlogin',
        {
            method: 'POST',
            headers: {
                'Content-Type':
                    'application/x-www-form-urlencoded;charset=UTF-8',
                'x-requested-by': 'ThoughtSpot',
            },
            cachePolicy: CachePolicies.NO_CACHE,
        },
    );
    function shortLivedLogin(userId: string, authToken: string): Promise<any> {
        const formData = new URLSearchParams();
        formData.append('id', userId);
        formData.append('shortlivedauthenticationtoken', authToken);
        return post(formData).then(handleLoginResponse);
    }
    React.useEffect(() => {
        if (response.ok) {
            const csrfToken = response.headers?.get('X-CSRF-Token');
            if (csrfToken) {
                setCSRFToken(csrfToken);
            }
        }
    }, [response, data]);

    return { shortLivedLogin };
};

export const useAuthentication = () => {
    const { doLogout } = useDoLogout();
    const { shortLivedLogin } = doShortLivedLogin();

    const authenticate = async (userGuid: string, authToken: string) => {
        try {
            await doLogout();
        } catch (error) {
            // Do nothing if already logged out
        }

        try {
            await shortLivedLogin(userGuid, authToken);
            return { authenticated: true };
        } catch (error) {
            return { authenticated: false, error };
        }
    };

    return { authenticate };
};

export const refetchPreauthInfo = async () => {
    (window as any).preauthInfo = await fetchPreauthInfo();
    const data = await (window as any)?.preauthInfo;
    if (data) {
        const authInfo = await data.clone().json();
        updateServices(authInfo);
    }
};
